import React, { useEffect } from 'react';
import { bool, string } from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { copy } from '@thd-olt-functional/utils';
import { useStoreId } from '@thd-nucleus/experience-context';
import {
  arrayOf as arrayType,
  number as numberType,
  params,
  shape as shapeType,
  string as stringType,
  bool as boolType
} from '@thd-nucleus/data-sources';
import {
  useProductDeprecated,
  useReviewsDeprecated
} from '@thd-nucleus/data-sources/react/dataModel/migration';
import { ProductStructuredData, BreadcrumbStructuredData, VideoStructuredData } from './structured-data';

function formatDescription(description) {
  if (!description) return '';
  const MAX_LEN = 199;
  if (description.length < MAX_LEN || description[MAX_LEN] === ' ') return description;
  let nextSpace = description.indexOf(' ', MAX_LEN);
  return description.substring(0, nextSpace);
}

const ProductMetadata = ({
  itemId, reviewsCanonical, pageNumber, pageTitle, pageDescription
}) => {
  const storeId = useStoreId();

  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('product-metadata.ready'); }, []);

  const { data, loading, error } = useProductDeprecated({
    itemId,
    storeId,
    domains: [
      'identifiers',
      'details',
      'media',
      'taxonomy',
      'availabilityType',
      'reviews',
      'pricing',
      'specificationGroup'
    ],
    alias: 'Metadata'
  });
  // const { data, loading, error } = useDataModel('product', {
  //   variables: { itemId, storeId }
  // });
  const { data: reviewData, loading: reviewLoading, reviewError } = useReviewsDeprecated({
    itemId, storeId
  });
  // const { data: reviewData, loading: reviewLoading, reviewError } = useDataModel('reviews',
  // { variables: { itemId, storeId }});
  if (reviewError && !reviewData) return null;
  if (reviewLoading) return null;
  if (!reviewData) return null;

  if (error && !data) return null;
  if (loading) return null;
  if (!data) return null;

  const {
    details,
    identifiers,
    seoDescription,
    seoKeywords,
    availabilityType,
    media,
    taxonomy,
    reviews,
    pricing,
    specificationGroup
  } = data?.product || {};

  // eslint-disable-next-line max-len
  const defaultTitle = `${identifiers?.brandName || ''} ${identifiers?.productLabel || ''}-${identifiers?.modelNumber || ''} - The Home Depot`.trim();
  const defaultDescription = formatDescription(seoDescription || details?.description);
  const title = pageTitle?.trim() || defaultTitle;
  const description = pageDescription || defaultDescription;
  const { canonicalUrl = '' } = identifiers || {};
  const identifierUrl = reviewsCanonical ? canonicalUrl.replace('/p/', '/p/reviews/') : canonicalUrl;
  const pageUrl = pageNumber ? identifierUrl.concat(`/${pageNumber}`) : identifierUrl;
  const canonical = `https://www.homedepot.com${pageUrl}`;
  const alternate = `android-app://com.thehomedepot/homedepot/product/${itemId}`;
  const { video, image } = media || {};
  const primaryImage = image?.url;
  let robotsTag;
  if (availabilityType?.discontinued) {
    robotsTag = <meta name="robots" content="noindex" />;
  }
  return (
    <>
      <Helmet>
        <meta name="description" content={description} />
        <meta name="keywords" content={seoKeywords} />
        <meta name="og:description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={primaryImage} />
        <meta property="og:type" content="product" />
        <meta property="og:site_name" content="The Home Depot" />
        <meta property="fb:admins" content="100005416960152" />
        {robotsTag}
        <title>{title}</title>
        <link rel="canonical" href={canonical} />
        <link rel="alternate" href={alternate} />
      </Helmet>
      <ProductStructuredData
        aggregateReviews={reviews?.ratingsReviews}
        reviews={reviewData?.reviews?.Results}
        identifiers={identifiers}
        media={media}
        details={details}
        pricing={pricing}
        specifications={specificationGroup}
        availabilityType={availabilityType}
      />
      <BreadcrumbStructuredData
        canonical={canonical}
        breadcrumbs={copy(taxonomy?.breadCrumbs || []).data}
        title={title}
      />
      { !!video?.length
      && (
        <VideoStructuredData
          videos={video}
          productLabel={identifiers?.productLabel || ''}
        />
      )}
    </>
  );
};

ProductMetadata.dataModel = {
  product: params({ itemId: stringType().isRequired() }).shape({
    availabilityType: shapeType({
      discontinued: boolType(),
      status: boolType()
    }),
    details: shapeType({
      description: stringType()
    }),
    identifiers: shapeType({
      brandName: stringType(),
      canonicalUrl: stringType(),
      itemId: stringType(),
      modelNumber: stringType(),
      productLabel: stringType(),
      storeSkuNumber: stringType(),
      upcGtin13: stringType()
    }),
    media: shapeType({
      image: shapeType({
        url: stringType()
      }).client(),
      images: arrayType(shapeType({
        url: stringType()
      })),
      video: arrayType(shapeType({
        shortDescription: stringType(),
        thumbnail: stringType(),
        url: stringType(),
        videoStill: stringType()
      }))
    }),
    pricing: params({ storeId: stringType() }).shape({
      promotion: shapeType({
        dates: shapeType({
          end: stringType()
        })
      }),
      value: numberType({ float: true })
    }),
    reviews: shapeType({
      ratingsReviews: shapeType({
        averageRating: stringType(),
        totalReviews: stringType()
      })
    }),
    seoDescription: stringType(),
    specificationGroup: arrayType(shapeType({
      specifications: arrayType(shapeType({
        specName: stringType(),
        specValue: stringType()
      })),
      specTitle: stringType()
    })),
    taxonomy: shapeType({
      breadCrumbs: arrayType(shapeType({
        label: stringType(),
        url: stringType()
      }))
    })
  }),
  reviews: params({ itemId: stringType().isRequired() }).shape({
    Results: arrayType(shapeType({
      Rating: numberType(),
      ReviewText: stringType(),
      Title: stringType(),
      UserNickname: stringType()
    }))
  })
};

ProductMetadata.propTypes = {
  itemId: string.isRequired,
  reviewsCanonical: bool,
  pageNumber: string,
  pageTitle: string,
  pageDescription: string
};

ProductMetadata.defaultProps = {
  reviewsCanonical: false,
  pageNumber: null,
  pageTitle: null,
  pageDescription: null
};

export { ProductMetadata };
